import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  Clapperboard,
  Clock,
  Video,
  MessageCircle,
  Mic,
  Settings2,
  Sparkles,
  Film,
  Image as ImageIcon,
  User,
  MapPin,
  Box,
  Maximize2,
  Upload,
  X,
  Plus,
  Trash2,
  Eye,
  MonitorPlay,
  Pencil,
  History,
  LayoutGrid,
  Loader2,
  FolderOpen,
  RefreshCw,
  Check,
} from "lucide-react";
import MultiSelect from "../common/MultiSelect";
import VideoSettingsPanel from "../common/VideoSettingsPanel";
import ShotReferenceSelector from "../common/ShotReferenceSelector";
import HistoryPanel from "../common/HistoryPanel";
import { CONTINUITY_OPTIONS, VIDEO_MODELS } from "../common/Constants";
import { authenticatedFetch } from "../../../utils/api";
import { API_HOST } from "../../../utils/constants";
import { useTheme } from "../../../context/ThemeContext";
import { useToast } from "../../../context/ToastContext";
import ResourceSelector from "../../resource/ResourceSelector";
import SketchEditorModal from "../../common/SketchEditorModal";
import { ConfirmDialog } from '@ui';
import { runTaskToCompletion } from "../../../services/taskService";
import { useDelayedUnmount } from "../../../hooks/useDelayedUnmount";
import {
  getProjectVideoProvider,
  supportsVideoTaskCancel,
} from "../../../utils/projectRuntime";
import { isWanxVideoModel, normalizeWanxVideoModel } from "../../../utils/wanxVideoCapabilities";

const ShotGalleryView = ({
  rows,
  isGlobalLocked,
  selectedIndex,
  setSelectedIndex,
  characterOptions,
  sceneOptions,
  propOptions,
  handleChange,
  handleGenerateImage,
  handleGenerateImageBatch,
  handleGenerateVideo,
  stopVideoGeneration,
  stopImageGeneration,
  onOpenWorkstation,
  handleLocalImageUpload,
  handleInsertRow,
  handleDeleteRow,
  handleSketchUpload,
  currentRatio = "16:9",
  onOpenAudio,
  projectId,
  onOpenMultiShot,
  videoEnabled = true,
}) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const toast = useToast();
  const currentShot = rows?.[selectedIndex];
  const scrollRef = useRef(null);
  const fileInputRefs = useRef({});
  const sketchInputRef = useRef(null);

  const [showVideoSettings, setShowVideoSettings] = useState(false);
  const settingsBtnRef = useRef(null);
  const [settingsPortalPos, setSettingsPortalPos] = useState(null);
  const [isGeneratingGridPrompt, setIsGeneratingGridPrompt] = useState(false);
  const [previewOverride, setPreviewOverride] = useState(null);

  // 历史面板状态
  const [showImageHistory, setShowImageHistory] = useState(false);
  const [showVideoHistory, setShowVideoHistory] = useState(false);

  // 出场动画：历史记录弹层延迟卸载
  const imageHistoryAnim = useDelayedUnmount(showImageHistory, 150);
  const videoHistoryAnim = useDelayedUnmount(videoEnabled && showVideoHistory, 150);
  // 出场动画：视频设置抽屉延迟卸载（缓存位置供退场渲染）
  const videoSettingsAnim = useDelayedUnmount(videoEnabled && showVideoSettings && !!settingsPortalPos, 150);
  const lastSettingsPosRef = useRef(null);
  if (settingsPortalPos) lastSettingsPosRef.current = settingsPortalPos;
  const [showImageResourceSelector, setShowImageResourceSelector] = useState(false);
  const [showVideoResourceSelector, setShowVideoResourceSelector] = useState(false);

  // 候选图片展开状态
  const [showCandidates, setShowCandidates] = useState(false);
  const candidatesAnim = useDelayedUnmount(showCandidates, 150);

  // 响应式侧栏
  const [showLeftPanel, setShowLeftPanel] = useState(true);
  const [showRightPanel, setShowRightPanel] = useState(true);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1024px)');
    if (mq.matches) {
      setShowLeftPanel(false);
      setShowRightPanel(false);
    }
    const handler = (e) => {
      if (e.matches) {
        setShowLeftPanel(false);
        setShowRightPanel(false);
      }
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // 删除确认状态
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState(null);
  const [showSketchEditor, setShowSketchEditor] = useState(false);

  useEffect(() => {
    setPreviewOverride(null);
    setShowImageHistory(false);
    setShowVideoHistory(false);
    setShowCandidates(false);
  }, [selectedIndex]);

  useEffect(() => {
    if (videoEnabled) return;
    setShowVideoSettings(false);
    setShowVideoHistory(false);
    setShowVideoResourceSelector(false);
    setPreviewOverride((current) => current === "video" ? "image" : current);
  }, [videoEnabled]);

  // 候选图片数量变化时自动展开
  useEffect(() => {
    if (currentShot?.image_candidates?.length > 1) {
      setShowCandidates(true);
    }
  }, [currentShot?.image_candidates?.length]);

  const handleRestore = (item) => {
    if (item.type === "image") {
      handleChange(selectedIndex, "generated_image", item.url);
      handleChange(selectedIndex, "image_prompt", item.prompt);
      setShowImageHistory(false);
    } else {
      handleChange(selectedIndex, "generated_video", item.url);
      handleChange(selectedIndex, "video_prompt", item.prompt);
      const historyProvider = String(item.provider || item.model || '').trim().toLowerCase();
      const historyModel = String(item.sub_model || item.actual_model || '').trim().toLowerCase();
      if (historyProvider === 'wanx' || isWanxVideoModel(historyProvider) || isWanxVideoModel(historyModel)) {
        const restoredWanxModel = normalizeWanxVideoModel(historyModel || historyProvider);
        handleChange(selectedIndex, 'video_provider', 'wanx');
        handleChange(selectedIndex, 'video_model', 'wanx');
        handleChange(selectedIndex, 'video_sub_model', restoredWanxModel);
        handleChange(selectedIndex, 'video_wan_mode', item.mode || 'first_frame');
        handleChange(selectedIndex, 'video_wan_duration', item.duration ?? 5);
        handleChange(selectedIndex, 'video_wan_resolution', item.resolution || '1080P');
        handleChange(selectedIndex, 'video_wan_ratio', item.ratio || 'adaptive');
        handleChange(selectedIndex, 'video_wan_audio', item.audio ?? (item.sound !== 'off'));
        handleChange(selectedIndex, 'video_wan_watermark', item.watermark === true);
        handleChange(selectedIndex, 'video_wan_prompt_extend', item.promptExtend !== false);
        handleChange(selectedIndex, 'video_wan_seed', item.seed ?? null);
        handleChange(selectedIndex, 'video_wan_media', Array.isArray(item.wanxMedia)
          ? item.wanxMedia.map((media) => ({ ...media }))
          : []);
      }
      setShowVideoHistory(false);
    }
  };

  // 处理资源库选择
  const handleSelectResource = (resource) => {
    if (resource.typeCode === 'storyboard_video') {
      handleChange(selectedIndex, 'generated_video', resource.url);
    } else {
      handleChange(selectedIndex, 'generated_image', resource.url);
    }
  };

  const activeView = (() => {
    if (previewOverride === "image") return "image";
    if (videoEnabled && previewOverride === "video") return "video";
    if (videoEnabled && currentShot?.generated_video) return "video";
    if (currentShot?.generated_image) return "image";
    return "empty";
  })();

  const storedVideoProvider = currentShot?.video_provider
    || currentShot?.videoProvider
    || currentShot?.video_model
    || currentShot?.videoModel
    || "vidu";
  const currentVideoProvider = isWanxVideoModel(storedVideoProvider)
    ? 'wanx'
    : storedVideoProvider;
  const canCancelVideoTask = supportsVideoTaskCancel(
    getProjectVideoProvider(projectId, currentVideoProvider) || currentVideoProvider,
  );
  const currentVideoModel = VIDEO_MODELS?.find(m => m.id === currentVideoProvider)
    || (VIDEO_MODELS?.[0] || { name: '加载中...', icon: Settings2 });

  useEffect(() => {
    if (rows?.length > 0 && selectedIndex >= rows.length) {
      setSelectedIndex(Math.max(0, rows.length - 1));
    }
  }, [rows?.length, selectedIndex, setSelectedIndex]);

  useEffect(() => {
    if (showVideoSettings && settingsBtnRef.current) {
      const rect = settingsBtnRef.current.getBoundingClientRect();
      setSettingsPortalPos({ bottom: window.innerHeight - rect.top + 4, right: window.innerWidth - rect.right });
    } else {
      setSettingsPortalPos(null);
    }
  }, [showVideoSettings]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showVideoSettings &&
        settingsBtnRef.current &&
        !settingsBtnRef.current.contains(event.target) &&
        !event.target.closest(".video-settings-panel")
      ) {
        setShowVideoSettings(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showVideoSettings]);

  useEffect(() => {
    if (scrollRef.current) {
      const elementIndex = selectedIndex * 2 + 1;
      const selectedEl = scrollRef.current.children[elementIndex];
      if (selectedEl) {
        selectedEl.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  }, [selectedIndex]);

  const onFileSelect = (e, index) => {
    const file = e.target.files[0];
    if (file) handleLocalImageUpload(index, file);
  };

  const onSketchSelect = (e) => {
    const file = e.target.files[0];
    if (file && handleSketchUpload) {
      handleSketchUpload(selectedIndex, file);
    }
  };

  const handleGenerateConsistencyPrompt = async () => {
    if (!currentShot.visual_content && !currentShot.action) {
      toast.warning("请至少填写【画面描述】或【视频指导】才能生成连贯性提示词");
      return;
    }
    setIsGeneratingGridPrompt(true);
    try {
      const { result } = await runTaskToCompletion("GENERATE_CONSISTENCY_GRID_PROMPT", {
        visual: currentShot.visual_content || "",
        action: currentShot.action || "",
        dialogue: currentShot.content || "",
        audio: currentShot.audio || "",
      }, { source: "storyboard_consistency_prompt" });
      const gridPrompt = typeof result === "string" ? result : (result?.value || result?.result || "");
      handleChange(selectedIndex, "image_prompt", gridPrompt);
    } catch (e) {
      toast.error("生成失败: " + e.message);
    } finally {
      setIsGeneratingGridPrompt(false);
    }
  };

  const getAssets = (ids, options) => {
    if (!ids || ids.length === 0) return [];
    return ids
      .map((id) => options.find((opt) => opt.id === id))
      .filter(Boolean);
  };

  const activeChars = currentShot
    ? getAssets(currentShot.refCharacterIds, characterOptions)
    : [];
  const activeScenes = currentShot
    ? getAssets(currentShot.refSceneIds, sceneOptions)
    : [];
  const activeProps = currentShot
    ? getAssets(currentShot.refPropIds, propOptions)
    : [];

  const getGridColsClass = (count) => {
    if (count <= 1) return "grid-cols-1";
    if (count === 2) return "grid-cols-2";
    if (count === 3) return "grid-cols-3";
    return "grid-cols-2";
  };

  const renderResourceGrid = (items) => {
    if (!items || items.length === 0) return null;
    const gridClass = getGridColsClass(items.length);
    return (
      <div className={`grid ${gridClass} gap-2 pt-2 transition-all`}>
        {items.map((item) => (
          <div
            key={item.id}
            className="group relative rounded-lg overflow-hidden shadow-lg"
            style={{ backgroundColor: 'var(--color-bg-overlay)', border: '1px solid var(--color-border-primary)' }}
          >
            <img
              src={item.imageUrl}
              className="w-full h-auto block"
              alt={item.name}
            />
            <div className="absolute bottom-0 inset-x-0 p-1 backdrop-blur-sm" style={{ backgroundColor: 'var(--overlay-medium)', borderTop: '1px solid var(--color-border-primary)' }}>
              <div
                className="font-bold truncate"
                style={{ color: 'white', fontSize: items.length >= 3 ? '9px' : '10px' }}
              >
                {item.name}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const InsertTrigger = ({ index }) => {
    return (
      <div
        onClick={() => handleInsertRow(index)}
        className="group/trigger w-4 h-full flex items-center justify-center cursor-pointer hover:w-8 transition-all duration-300 relative shrink-0 z-20"
        title="在此处插入新分镜"
      >
        <div className="absolute inset-0 bg-transparent"></div>
        <div className="absolute opacity-0 group-hover/trigger:opacity-100 rounded-full p-0.5 shadow-lg transform scale-0 group-hover/trigger:scale-100 transition-all" style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}>
          <Plus size={10} strokeWidth={3} />
        </div>
      </div>
    );
  };

  if (!currentShot)
    return <div className="text-center mt-20" style={{ color: 'var(--color-text-muted)' }}>暂无数据</div>;

  return (
    <div className="flex flex-col h-full w-full overflow-hidden" style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
      <div className="flex-1 flex overflow-hidden border-b" style={{ borderColor: 'var(--color-border-primary)' }}>
        {/* 左侧栏切换按钮 */}
        {!showLeftPanel && (
          <button
            onClick={() => setShowLeftPanel(true)}
            className="shrink-0 w-8 flex items-center justify-center border-r transition-all duration-200 hover:bg-[var(--color-bg-tertiary)]"
            style={{ borderColor: 'var(--color-border-primary)', color: 'var(--color-text-muted)' }}
            aria-label="展开左侧面板"
            title="展开左侧面板"
          >
            <Clapperboard size={14} />
          </button>
        )}
        {showLeftPanel && <div className="w-80 border-r p-4 pb-40 overflow-y-auto custom-scrollbar flex flex-col gap-6 shrink-0 relative" style={{ backgroundColor: 'var(--color-bg-secondary)', borderColor: 'var(--color-border-primary)' }}>
          <button
            onClick={() => setShowLeftPanel(false)}
            className="absolute top-2 right-2 p-1 rounded hover:bg-[var(--color-bg-tertiary)] transition-all duration-200"
            style={{ color: 'var(--color-text-muted)' }}
            aria-label="收起左侧面板"
            title="收起左侧面板"
          >
            <X size={12} />
          </button>
          <div className="flex items-start justify-between gap-4 border-b pb-4" style={{ borderColor: 'var(--color-border-primary)' }}>
            <div className="flex flex-col gap-3">
              <div className="text-4xl font-black font-mono leading-none" style={{ color: 'var(--color-text-muted)', opacity: 0.2 }}>
                #{currentShot.shot_number}
              </div>
              <span className="text-2xs font-mono" style={{ color: 'var(--color-text-muted)', opacity: 0.4 }}>id:{currentShot.id}</span>
            </div>

            <div className="flex-1 space-y-2 pt-1 flex flex-col items-end">
              <div className="flex items-center gap-2 p-1.5 rounded border w-full" style={{ backgroundColor: 'var(--color-bg-overlay)', borderColor: 'var(--color-border-primary)' }}>
                <Video size={12} style={{ color: theme.colors.accentPrimary }} />
                <input
                  value={currentShot.shot}
                  onChange={(e) =>
                    handleChange(selectedIndex, "shot", e.target.value)
                  }
                  className="bg-transparent text-xs w-full outline-none"
                  style={{ color: 'var(--color-text-primary)' }}
                  placeholder="景别"
                />
              </div>
              <div className="flex items-center gap-2 p-1.5 rounded border w-full" style={{ backgroundColor: 'var(--color-bg-overlay)', borderColor: 'var(--color-border-primary)' }}>
                <Clock size={12} style={{ color: theme.colors.bluePrimary }} />
                <input
                  value={currentShot.time}
                  onChange={(e) =>
                    handleChange(selectedIndex, "time", e.target.value)
                  }
                  className="bg-transparent text-xs w-full outline-none"
                  style={{ color: 'var(--color-text-primary)' }}
                  placeholder="时长"
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <label className="text-2xs font-bold uppercase flex items-center gap-1" style={{ color: 'var(--color-text-muted)' }}>
                  <Pencil size={12} /> 草图参考 (Sketch)
                </label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowSketchEditor(true)}
                    className="text-2xs flex items-center gap-1 hover:underline"
                    style={{ color: 'var(--color-text-muted)' }}
                    title="绘制草图"
                  >
                    <Pencil size={10} /> 绘制
                  </button>
                  {currentShot.sketch_preview && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleChange(selectedIndex, "sketch_preview", null);
                        handleChange(selectedIndex, "sketch_base64", null);
                      }}
                      className="text-2xs hover:underline"
                      style={{ color: 'var(--color-error)' }}
                    >
                      清除草图
                    </button>
                  )}
                </div>
              </div>

              <input
                type="file"
                ref={sketchInputRef}
                className="hidden"
                accept="image/*"
                onChange={onSketchSelect}
              />

              {currentShot.sketch_preview ? (
                <div
                  className="relative w-full aspect-video rounded-lg border overflow-hidden cursor-pointer group/preview"
                  onClick={() => sketchInputRef.current?.click()}
                  title="点击更换草图"
                  style={{ backgroundColor: 'var(--color-bg-overlay)', borderColor: 'var(--color-border-primary)' }}
                >
                  <img
                    src={currentShot.sketch_preview}
                    className="w-full h-full object-contain opacity-80 group-hover/preview:opacity-100 transition-opacity"
                  />
                  <div className="absolute inset-0 flex items-center justify-center gap-3 opacity-0 group-hover/preview:opacity-100 transition-opacity" style={{ backgroundColor: 'var(--overlay-light)' }}>
                    <Upload size={18} style={{ color: 'white' }} />
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowSketchEditor(true); }}
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }}
                      title="重新绘制"
                    >
                      <Pencil size={18} />
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="w-full aspect-video rounded-lg border border-dashed flex items-center justify-center gap-4 cursor-pointer transition-all"
                  style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-muted)' }}
                >
                  <button
                    onClick={() => sketchInputRef.current?.click()}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-lg transition-all duration-200 hover:bg-[var(--color-bg-tertiary)]"
                  >
                    <Upload size={18} />
                    <span className="text-2xs">上传</span>
                  </button>
                  <button
                    onClick={() => setShowSketchEditor(true)}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-lg transition-all duration-200 hover:bg-[var(--color-bg-tertiary)]"
                  >
                    <Pencil size={18} />
                    <span className="text-2xs">绘制</span>
                  </button>
                </div>
              )}

              {ReactDOM.createPortal(
                <SketchEditorModal
                  isOpen={showSketchEditor}
                  aspectRatio={currentRatio || '16:9'}
                  onSave={(cosUrl) => {
                    handleChange(selectedIndex, "sketch_preview", cosUrl);
                    handleChange(selectedIndex, "sketch_base64", cosUrl);
                    setShowSketchEditor(false);
                  }}
                  onClose={() => setShowSketchEditor(false)}
                  title={`构思草图 #${selectedIndex + 1}`}
                />,
                document.body
              )}
            </div>

            <div className="space-y-1">
              <label className="text-2xs font-bold uppercase flex items-center gap-1" style={{ color: 'var(--color-text-muted)' }}>
                <Clapperboard size={12} /> 画面描述
              </label>
              <textarea
                value={currentShot.visual_content || ""}
                onChange={(e) =>
                  handleChange(selectedIndex, "visual_content", e.target.value)
                }
                className="w-full rounded-lg p-2 text-xs outline-none resize-none h-20"
                style={{ backgroundColor: 'var(--color-bg-overlay)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-secondary)' }}
              />
            </div>
            <div className="space-y-1">
              <label className="text-2xs font-bold uppercase flex items-center gap-1" style={{ color: 'var(--color-text-muted)' }}>
                <Settings2 size={12} /> 摄影指导
              </label>
              <textarea
                value={currentShot.action || ""}
                onChange={(e) =>
                  handleChange(selectedIndex, "action", e.target.value)
                }
                className="w-full rounded-lg p-2 text-xs outline-none resize-none h-16"
                style={{ backgroundColor: 'var(--color-bg-overlay)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-secondary)' }}
              />
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <label className="text-2xs font-bold uppercase flex items-center gap-1" style={{ color: 'var(--color-text-muted)' }}>
                  <MessageCircle size={12} /> 对白 / 台词
                </label>

                <button
                  onClick={() => onOpenAudio(selectedIndex, currentShot.content)}
                  className="text-2xs flex items-center gap-1 px-2 py-0.5 rounded border transition-all"
                  style={{
                    backgroundColor: currentShot.audio ? 'var(--color-blue-light)' : 'var(--color-bg-primary)',
                    color: currentShot.audio ? theme.colors.bluePrimary : 'var(--color-text-muted)',
                    borderColor: currentShot.audio ? theme.colors.bluePrimary : 'var(--color-border-primary)'
                  }}
                  title="AI 语音合成"
                >
                  <Mic size={10} />
                  {currentShot.audio ? "重配" : "配音"}
                </button>
              </div>

              <textarea
                value={currentShot.content || ""}
                onChange={(e) =>
                  handleChange(selectedIndex, "content", e.target.value)
                }
                className="w-full rounded-lg p-2 text-xs outline-none resize-none h-16"
                style={{ backgroundColor: 'var(--color-bg-overlay)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-secondary)' }}
                placeholder="输入角色台词..."
              />

              {/* 🔥 新增：播放器 */}
              {currentShot.audio && currentShot.audio.startsWith("http") && (
                <div className="mt-1">
                  <audio
                    controls
                    src={currentShot.audio}
                    className="w-full h-8 block rounded"
                    style={{ backgroundColor: 'var(--color-bg-overlay)', border: '1px solid var(--color-border-primary)' }}
                  />
                </div>
              )}
            </div>

            <div className="space-y-1">
              <label className="text-2xs font-bold uppercase flex items-center gap-1" style={{ color: 'var(--color-text-muted)' }}>
                <Mic size={12} /> 音效备注
              </label>
              <textarea
                value={currentShot.audio_desc || ""}
                onChange={(e) =>
                  handleChange(selectedIndex, "audio_desc", e.target.value)
                }
                className="w-full rounded-lg p-2 text-xs outline-none resize-none h-12"
                style={{ backgroundColor: 'var(--color-bg-overlay)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-secondary)' }}
                placeholder="环境音、拟音..."
              />
            </div>
          </div>

          {selectedIndex > 0 && (
            <div className="rounded-xl p-3 flex flex-col gap-2 mt-2" style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-1 h-3 rounded-full" style={{ backgroundColor: theme.colors.accentPrimary }}></div>
                  <span className="text-2xs font-bold uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}>
                    连戏控制
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <ShotReferenceSelector
                    currentIndex={selectedIndex}
                    rows={rows}
                    selectedIndex={currentShot.ref_shot_index}
                    onChange={(val) =>
                      handleChange(selectedIndex, "ref_shot_index", val)
                    }
                    align="right"
                  />
                  {currentShot.ref_shot_index !== null &&
                    currentShot.ref_shot_index !== undefined && (
                      <button
                        onClick={() =>
                          handleChange(selectedIndex, "ref_shot_index", null)
                        }
                        className="p-1 rounded transition-all duration-200"
                        style={{ color: 'var(--color-text-muted)' }}
                      >
                        <X size={10} />
                      </button>
                    )}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {CONTINUITY_OPTIONS.map((mode) => (
                  <button
                    key={mode.id || "none"}
                    onClick={() =>
                      handleChange(selectedIndex, "continuity_mode", mode.id)
                    }
                    className="px-2 py-1 rounded text-2xs border transition-all flex items-center gap-1.5"
                    style={{
                      backgroundColor: currentShot.continuity_mode === mode.id ? 'var(--color-accent-light)' : 'transparent',
                      borderColor: currentShot.continuity_mode === mode.id ? theme.colors.accentPrimary : 'var(--color-border-primary)',
                      color: currentShot.continuity_mode === mode.id ? theme.colors.accentSecondary : 'var(--color-text-muted)'
                    }}
                  >
                    {mode.icon} {mode.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>}

        <div className="flex-1 flex flex-col min-w-0" style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
          <div className="flex-1 relative flex items-center justify-center p-4 min-h-0 overflow-hidden group/main">
            <div
              className="relative flex items-center justify-center shadow-2xl rounded-lg border overflow-hidden"
              style={{
                backgroundColor: 'var(--color-bg-overlay)',
                borderColor: 'var(--color-border-primary)',
                aspectRatio: currentRatio.replace(":", "/"),
                maxHeight: "100%",
                maxWidth: "100%",
              }}
            >
              {activeView === "video" ? (
                currentShot.generated_video ? (
                  <video
                    key={currentShot.generated_video}
                    src={currentShot.generated_video}
                    controls
                    autoPlay
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2" style={{ color: 'var(--color-text-muted)' }}>
                    <Film size={48} strokeWidth={1} />
                    <span className="text-xs">暂无视频</span>
                  </div>
                )
              ) : activeView === "image" ? (
                currentShot.generated_image ? (
                  <div className="relative w-full h-full">
                    <div
                      className="w-full h-full cursor-zoom-in group/img"
                      onClick={() => onOpenWorkstation(currentShot.generated_image)}
                    >
                      <img
                        key={currentShot.generated_image}
                        src={currentShot.generated_image}
                        className="w-full h-full object-contain animate-in fade-in duration-300"
                      />

                      {/* 重新生成时的半透明遮罩 Loading */}
                      {currentShot.isGenerating && (
                        <div
                          className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-3"
                          style={{ backgroundColor: 'var(--overlay-medium)', backdropFilter: 'blur(4px)' }}
                        >
                          <Wand2 className="animate-spin" size={32} style={{ color: theme.colors.accentPrimary }} />
                          <span className="text-sm font-medium" style={{ color: 'white' }}>
                            {currentShot.image_candidates?.length > 0
                              ? `生成中 ${currentShot.image_candidates.length}/4`
                              : '重新生成中...'}
                          </span>
                          <div className="w-48 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                            <div
                              className="h-full rounded-full transition-all duration-500"
                              style={{
                                width: `${(currentShot.image_candidates?.length || 0) * 25}%`,
                                backgroundColor: theme.colors.accentPrimary,
                                boxShadow: `0 0 8px ${theme.colors.accentPrimary}`
                              }}
                            />
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              stopImageGeneration(selectedIndex);
                            }}
                            className="px-3 py-1.5 rounded text-xs font-medium transition-all duration-200"
                            style={{ backgroundColor: 'var(--color-red-light)', color: 'var(--color-error)' }}
                          >
                            停止生成
                          </button>
                        </div>
                      )}

                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/img:opacity-100 transition-opacity pointer-events-none" style={{ backgroundColor: 'var(--overlay-light)' }}>
                        <span className="flex items-center gap-2 font-bold px-3 py-1.5 rounded-full border" style={{ backgroundColor: 'var(--overlay-medium)', color: 'var(--color-text-primary)', borderColor: 'var(--color-border-primary)' }}>
                          <Maximize2 size={14} /> 进入工作站
                        </span>
                      </div>
                    </div>
                    {/* 候选图片折叠条 */}
                    {currentShot.image_candidates?.length > 1 && (
                      <div className="absolute bottom-0 left-0 right-0 z-20">
                        <button
                          onClick={() => setShowCandidates(v => !v)}
                          className="w-full flex items-center justify-center gap-1 py-1 text-2xs font-medium backdrop-blur transition-all duration-200 hover:bg-[var(--color-bg-tertiary)]"
                          style={{ backgroundColor: 'rgba(0,0,0,0.55)', color: 'var(--color-text-secondary)' }}
                        >
                          {showCandidates ? '▾' : '▸'} 候选图 {currentShot.image_candidates.length} 张
                          {currentShot.isGenerating && (
                            <span className="ml-1 opacity-70">生成中...</span>
                          )}
                        </button>
                        {candidatesAnim.mounted && (
                          <div className={`flex gap-1.5 p-2 overflow-x-auto ${candidatesAnim.exiting ? "animate-out fade-out duration-150" : "animate-in fade-in duration-150"}`} style={{ backgroundColor: 'var(--overlay-heavy)' }}>
                            {currentShot.image_candidates.map((url, i) => (
                              <button
                                key={i}
                                onClick={() => handleChange(selectedIndex, 'generated_image', url)}
                                className="relative shrink-0 rounded overflow-hidden transition-all"
                                style={{
                                  width: 44, height: 44,
                                  border: currentShot.generated_image === url
                                    ? `2px solid ${theme.colors.accentPrimary}`
                                    : '2px solid transparent',
                                }}
                              >
                                <img src={url} className="w-full h-full object-cover" />
                                {currentShot.generated_image === url && (
                                  <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: 'var(--overlay-light)' }}>
                                    <Check size={14} style={{ color: theme.colors.accentPrimary }} />
                                  </div>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : currentShot.isGenerating ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
                    <div className="w-32 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
                      <div className="h-full rounded-full animate-pulse" style={{ width: '60%', backgroundColor: theme.colors.accentPrimary }} />
                    </div>
                    <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>图片生成中...</span>
                  </div>
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2" style={{ color: 'var(--color-text-muted)' }}>
                    <ImageIcon size={48} strokeWidth={1} />
                    <span className="text-xs">暂无图片</span>
                  </div>
                )
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2" style={{ color: 'var(--color-text-muted)' }}>
                  <div className="flex gap-2">
                    <ImageIcon size={32} />
                    {videoEnabled && <Film size={32} />}
                  </div>
                  <span className="text-xs">等待生成...</span>
                </div>
              )}
            </div>

            <div className="absolute top-4 right-4 flex gap-2 pointer-events-none z-10">
              {currentShot.isGenerating && (
                <div className="px-3 py-1 rounded-full text-xs flex items-center gap-2" style={{ backgroundColor: 'var(--color-blue-light)', color: theme.colors.bluePrimary }}>
                  <Sparkles size={12} className="animate-spin" />
                  {currentShot.image_candidates?.length > 0
                    ? `${currentShot.image_candidates.length}/4`
                    : '图片生成中...'}
                  <button
                    onClick={() => stopImageGeneration(selectedIndex)}
                    className="ml-1 px-2 py-0.5 rounded text-2xs pointer-events-auto"
                    style={{ backgroundColor: 'var(--color-red-light)', color: 'var(--color-error)' }}
                  >
                    停止
                  </button>
                </div>
              )}
              {videoEnabled && currentShot.isGeneratingVideo && (
                <span className="px-3 py-1 rounded-full text-xs flex items-center gap-2" style={{ backgroundColor: 'var(--color-blue-light)', color: theme.colors.bluePrimary }}>
                  <Film size={12} className="animate-spin" /> 视频生成中...
                </span>
              )}
              {videoEnabled && !currentShot.isGeneratingVideo && currentShot.video_task_status === 'failed' && currentShot.video_task_error && (
                <span
                  className="max-w-80 truncate px-3 py-1 rounded-full text-xs"
                  style={{ backgroundColor: 'var(--color-red-light)', color: 'var(--color-error)' }}
                  title={currentShot.video_task_error}
                >
                  视频失败：{currentShot.video_task_error}
                </span>
              )}
            </div>
          </div>

          <div className={`h-56 p-4 grid ${videoEnabled ? "grid-cols-2" : "grid-cols-1"} gap-6 border-t shrink-0 z-20 relative backdrop-blur`} style={{ backgroundColor: 'var(--color-bg-secondary)', borderColor: 'var(--color-border-primary)' }}>
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <label className="text-2xs font-bold uppercase flex items-center gap-1" style={{ color: 'var(--color-text-secondary)' }}>
                  <Sparkles size={12} style={{ color: theme.colors.accentPrimary }} /> 图片提示词
                </label>
                <div className="flex items-center gap-2 relative">
                  <div className="relative">
                    <button
                      onClick={() => setShowImageHistory(!showImageHistory)}
                      className="text-2xs flex items-center gap-1 px-2 py-1 rounded border transition-all"
                      style={{
                        backgroundColor: showImageHistory ? 'var(--color-accent-light)' : 'var(--color-bg-primary)',
                        borderColor: showImageHistory ? theme.colors.accentPrimary : 'var(--color-border-primary)',
                        color: showImageHistory ? 'white' : 'var(--color-text-muted)'
                      }}
                      title="查看历史记录"
                    >
                      <History size={10} /> 历史
                    </button>
                    {imageHistoryAnim.mounted && (
                      <div className="absolute bottom-full right-0 mb-2 z-50 w-72 h-64 shadow-2xl">
                        <HistoryPanel
                          historyList={currentShot.history_list}
                          type="image"
                          onRestore={handleRestore}
                          onClose={() => setShowImageHistory(false)}
                          exiting={imageHistoryAnim.exiting}
                        />
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => setPreviewOverride("image")}
                    className="text-2xs flex items-center gap-1 px-2 py-1 rounded border transition-all"
                    style={{
                      backgroundColor: activeView === "image" ? 'var(--color-accent-light)' : 'var(--color-bg-primary)',
                      borderColor: activeView === "image" ? theme.colors.accentPrimary : 'var(--color-border-primary)',
                      color: activeView === "image" ? theme.colors.accentSecondary : 'var(--color-text-muted)'
                    }}
                  >
                    <Eye size={10} /> 显示图片
                  </button>
                  <button
                    onClick={handleGenerateConsistencyPrompt}
                    disabled={isGeneratingGridPrompt}
                    className="text-2xs flex items-center gap-1 px-2 py-1 rounded border transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: 'var(--color-blue-light)', color: theme.colors.bluePrimary, borderColor: 'transparent' }}
                    title="生成 3x2 连贯分镜 Prompt"
                  >
                    {isGeneratingGridPrompt ? (
                      <Loader2 size={10} className="animate-spin" />
                    ) : (
                      <LayoutGrid size={10} />
                    )}{" "}
                    连贯性镜头
                  </button>
                  <button
                    onClick={() => setShowImageResourceSelector(true)}
                    className="text-2xs flex items-center gap-1 px-2 py-1 rounded border transition-all"
                    style={{
                      backgroundColor: 'var(--color-bg-primary)',
                      borderColor: 'var(--color-border-primary)',
                      color: 'var(--color-text-muted)'
                    }}
                    title="从资源库选择"
                  >
                    <FolderOpen size={10} /> 资源库
                  </button>
                  <button
                    onClick={() => handleGenerateImage(selectedIndex)}
                    disabled={currentShot.isGenerating}
                    className="text-2xs px-2 py-1 rounded transition-all duration-200 disabled:opacity-50 flex items-center gap-1"
                    style={{ backgroundColor: 'var(--color-accent-light)', color: theme.colors.accentSecondary, border: '1px solid var(--color-accent-primary)' }}
                    title="生成1张"
                  >
                    <RefreshCw size={10} />1
                  </button>
                  {handleGenerateImageBatch && (
                    <button
                      onClick={() => handleGenerateImageBatch(selectedIndex)}
                      disabled={currentShot.isGenerating}
                      className="text-2xs px-2 py-1 rounded transition-all duration-200 disabled:opacity-50 flex items-center gap-1"
                      style={{ backgroundColor: `${theme.colors.accentPrimary}25`, color: theme.colors.accentPrimary, border: `1px solid ${theme.colors.accentPrimary}` }}
                      title="生成4张候选图"
                    >
                      <RefreshCw size={10} />4
                    </button>
                  )}
                </div>
              </div>
              <textarea
                value={currentShot.image_prompt || ""}
                onChange={(e) =>
                  handleChange(selectedIndex, "image_prompt", e.target.value)
                }
                className="flex-1 w-full rounded-lg p-3 text-xs font-mono outline-none resize-none transition-all duration-200"
                style={{ backgroundColor: 'var(--color-bg-overlay)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-secondary)' }}
                placeholder="输入画面提示词..."
              />
            </div>

            {videoEnabled && <div className="flex flex-col gap-2 relative">
              <div className="flex justify-between items-center">
                <label className="text-2xs font-bold uppercase flex items-center gap-1" style={{ color: 'var(--color-text-secondary)' }}>
                  <Film size={12} style={{ color: theme.colors.bluePrimary }} /> 视频提示词
                </label>
                <div className="flex items-center gap-2 relative">
                  <div className="relative">
                    <button
                      onClick={() => setShowVideoHistory(!showVideoHistory)}
                      className="text-2xs flex items-center gap-1 px-2 py-1 rounded border transition-all"
                      style={{
                        backgroundColor: showVideoHistory ? 'var(--color-blue-light)' : 'var(--color-bg-primary)',
                        borderColor: showVideoHistory ? theme.colors.bluePrimary : 'var(--color-border-primary)',
                        color: showVideoHistory ? 'white' : 'var(--color-text-muted)'
                      }}
                      title="查看历史记录"
                    >
                      <History size={10} /> 历史
                    </button>
                    {videoHistoryAnim.mounted && (
                      <div className="absolute bottom-full right-0 mb-2 z-50 w-72 h-64 shadow-2xl">
                        <HistoryPanel
                          historyList={currentShot.history_list}
                          type="video"
                          onRestore={handleRestore}
                          onClose={() => setShowVideoHistory(false)}
                          exiting={videoHistoryAnim.exiting}
                        />
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => setPreviewOverride("video")}
                    className="text-2xs flex items-center gap-1 px-2 py-1 rounded border transition-all"
                    style={{
                      backgroundColor: activeView === "video" ? 'var(--color-blue-light)' : 'var(--color-bg-primary)',
                      borderColor: activeView === "video" ? theme.colors.bluePrimary : 'var(--color-border-primary)',
                      color: activeView === "video" ? theme.colors.bluePrimary : 'var(--color-text-muted)'
                    }}
                  >
                    <MonitorPlay size={10} /> 显示视频
                  </button>
                  <div className="relative" ref={settingsBtnRef}>
                    <button
                      onClick={() => setShowVideoSettings(!showVideoSettings)}
                      className={`flex items-center gap-1.5 px-2 py-1 rounded-full border transition-all text-2xs ${currentVideoModel.tagColor || ''}`}
                      style={{
                        borderColor: showVideoSettings ? theme.colors.bluePrimary : undefined,
                        backgroundColor: showVideoSettings ? `${theme.colors.bluePrimary}20` : undefined,
                      }}
                      title="视频生成设置"
                    >
                      <currentVideoModel.icon size={12} />
                      <span className="font-medium">{currentVideoModel.name.split(" ")[0]}</span>
                      <span className="text-2xs opacity-60">▾</span>
                    </button>
                    {videoSettingsAnim.mounted && (settingsPortalPos || lastSettingsPosRef.current) && ReactDOM.createPortal(
                      <div
                        className="fixed z-[200] video-settings-panel"
                        style={{ bottom: (settingsPortalPos || lastSettingsPosRef.current).bottom, right: (settingsPortalPos || lastSettingsPosRef.current).right }}
                      >
                        <VideoSettingsPanel
                          scene={currentShot}
                          index={selectedIndex}
                          rows={rows}
                          onClose={() => setShowVideoSettings(false)}
                          handleChange={handleChange}
                          onOpenMultiShot={onOpenMultiShot}
                          exiting={videoSettingsAnim.exiting}
                        />
                      </div>,
                      document.body
                    )}
                  </div>
                  <button
                    onClick={() => setShowVideoResourceSelector(true)}
                    className="text-2xs flex items-center gap-1 px-2 py-1 rounded border transition-all"
                    style={{
                      backgroundColor: 'var(--color-bg-primary)',
                      borderColor: 'var(--color-border-primary)',
                      color: 'var(--color-text-muted)'
                    }}
                    title="从资源库选择"
                  >
                    <FolderOpen size={10} /> 资源库
                  </button>
                  {currentShot.isGeneratingVideo && canCancelVideoTask ? (
                    <button
                      onClick={() => stopVideoGeneration(selectedIndex)}
                      className="text-2xs px-3 py-1 rounded transition-all duration-200"
                      style={{ backgroundColor: 'var(--color-red-light)', color: 'var(--color-error)', border: '1px solid var(--color-error)' }}
                    >
                      停止
                    </button>
                  ) : !currentShot.isGeneratingVideo ? (
                    <button
                      onClick={() => handleGenerateVideo(selectedIndex)}
                      disabled={currentShot.isGeneratingVideo}
                      className="text-2xs px-3 py-1 rounded transition-all duration-200 disabled:opacity-50"
                      style={{ backgroundColor: 'var(--color-blue-light)', color: theme.colors.bluePrimary, border: '1px solid var(--color-blue-primary)' }}
                    >
                      生成
                    </button>
                  ) : (
                    <span className="text-2xs px-3 py-1" style={{ color: 'var(--color-text-muted)' }}>
                      厂商任务生成中
                    </span>
                  )}
                </div>
              </div>
              <textarea
                value={currentShot.video_prompt || ""}
                onChange={(e) =>
                  handleChange(selectedIndex, "video_prompt", e.target.value)
                }
                className="flex-1 w-full rounded-lg p-3 text-xs font-mono outline-none resize-none transition-all duration-200"
                style={{ backgroundColor: 'var(--color-bg-overlay)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-secondary)' }}
                placeholder="输入视频提示词..."
              />
            </div>}
          </div>
        </div>

        {/* 右侧栏切换按钮 */}
        {!showRightPanel && (
          <button
            onClick={() => setShowRightPanel(true)}
            className="shrink-0 w-8 flex items-center justify-center border-l transition-all duration-200 hover:bg-[var(--color-bg-tertiary)]"
            style={{ borderColor: 'var(--color-border-primary)', color: 'var(--color-text-muted)' }}
            aria-label="展开右侧面板"
            title="展开右侧面板"
          >
            <User size={14} />
          </button>
        )}
        {showRightPanel && <div className="w-80 border-l p-4 overflow-y-auto custom-scrollbar flex flex-col gap-8 pb-20 shrink-0 relative" style={{ backgroundColor: 'var(--color-bg-secondary)', borderColor: 'var(--color-border-primary)' }}>
          <button
            onClick={() => setShowRightPanel(false)}
            className="absolute top-2 right-2 p-1 rounded hover:bg-[var(--color-bg-tertiary)] transition-all duration-200"
            style={{ color: 'var(--color-text-muted)' }}
            aria-label="收起右侧面板"
            title="收起右侧面板"
          >
            <X size={12} />
          </button>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-bold uppercase flex items-center gap-2" style={{ color: 'var(--color-text-secondary)' }}>
                <User size={14} style={{ color: theme.colors.bluePrimary }} /> 绑定角色
              </h4>
            </div>
            <MultiSelect
              label="添加/修改角色"
              icon={User}
              options={characterOptions}
              selectedIds={currentShot.refCharacterIds || []}
              onChange={(ids) =>
                handleChange(selectedIndex, "refCharacterIds", ids)
              }
              variantIndexes={currentShot.refCharacterVariantIndexes || []}
              onVariantChange={(indexes) =>
                handleChange(selectedIndex, "refCharacterVariantIndexes", indexes)
              }
              previewPlacement="left"
            />
            {renderResourceGrid(activeChars)}
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-bold uppercase flex items-center gap-2" style={{ color: 'var(--color-text-secondary)' }}>
                <MapPin size={14} style={{ color: theme.colors.greenPrimary }} /> 绑定场景
              </h4>
            </div>
            <MultiSelect
              label="添加/修改场景"
              icon={MapPin}
              options={sceneOptions}
              selectedIds={currentShot.refSceneIds || []}
              onChange={(ids) =>
                handleChange(selectedIndex, "refSceneIds", ids)
              }
              variantIndexes={currentShot.refSceneVariantIndexes || []}
              onVariantChange={(indexes) =>
                handleChange(selectedIndex, "refSceneVariantIndexes", indexes)
              }
              previewPlacement="left"
            />
            {renderResourceGrid(activeScenes)}
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-bold uppercase flex items-center gap-2" style={{ color: 'var(--color-text-secondary)' }}>
                <Box size={14} style={{ color: theme.colors.accentPrimary }} /> 绑定道具
              </h4>
            </div>
            <MultiSelect
              label="添加/修改道具"
              icon={Box}
              options={propOptions}
              selectedIds={currentShot.refPropIds || []}
              onChange={(ids) => handleChange(selectedIndex, "refPropIds", ids)}
              variantIndexes={currentShot.refPropVariantIndexes || []}
              onVariantChange={(indexes) =>
                handleChange(selectedIndex, "refPropVariantIndexes", indexes)
              }
              previewPlacement="left"
            />
            {renderResourceGrid(activeProps)}
          </div>
        </div>}
      </div>

      <div
        className="h-40 border-t p-3 flex items-center overflow-x-auto overflow-y-hidden custom-scrollbar shrink-0 z-10"
        ref={scrollRef}
        style={{ backgroundColor: 'var(--color-bg-overlay)', borderColor: 'var(--color-border-primary)' }}
      >
        <InsertTrigger index={0} />
        {rows?.map((row, index) => (
          <React.Fragment key={row.id || index}>
            <div
              onClick={() => setSelectedIndex(index)}
              className="relative shrink-0 h-28 rounded-lg overflow-hidden border-2 cursor-pointer transition-all group flex flex-col"
              style={{
                aspectRatio: currentRatio.replace(":", "/"),
                backgroundColor: 'var(--color-bg-secondary)',
                borderColor: selectedIndex === index ? theme.colors.accentPrimary : 'var(--color-border-primary)',
                opacity: selectedIndex === index ? 1 : 0.7,
                transform: selectedIndex === index ? 'scale(1.05)' : 'scale(1)',
                zIndex: selectedIndex === index ? 10 : 1,
              }}
            >
              <div className="flex-1 relative overflow-hidden">
                {row.generated_image ? (
                  <img
                    src={row.generated_image}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-1" style={{ color: 'var(--color-text-muted)' }}>
                    <ImageIcon size={20} />
                    <span className="text-2xs">无图片</span>
                  </div>
                )}
                <div className="absolute inset-0 flex flex-col justify-end p-2 pointer-events-none" style={{ background: 'var(--gradient-card-bottom)' }}>
                  <div className="flex justify-between items-center">
                    <span
                      className="text-xs font-black drop-shadow-md"
                      style={{ color: selectedIndex === index ? theme.colors.accentPrimary : 'white' }}
                    >
                      #{row.shot_number}
                    </span>
                    <span className="text-2xs font-mono drop-shadow-md" style={{ color: 'rgba(255,255,255,0.5)' }}>id:{row.id}</span>
                    <div className="flex items-center gap-1">
                      {row.image_candidates?.length > 1 && (
                        <span className="text-2xs px-1 rounded drop-shadow-md font-bold" style={{ backgroundColor: 'var(--overlay-medium)', color: theme.colors.accentPrimary }}>
                          {row.image_candidates.length}
                        </span>
                      )}
                      {videoEnabled && row.generated_video && (
                        <Film
                          size={12}
                          className="drop-shadow-md"
                          style={{ color: theme.colors.accentPrimary }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {!isGlobalLocked && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteConfirmIndex(index);
                  }}
                  className="absolute top-1 left-1 opacity-0 group-hover:opacity-100 p-1 rounded-full backdrop-blur border transition-all z-20"
                  style={{ backgroundColor: 'var(--overlay-medium)', color: 'var(--color-text-primary)', borderColor: 'var(--color-border-primary)' }}
                  aria-label="删除分镜"
                  title="删除分镜"
                >
                  <Trash2 size={10} />
                </button>
              )}

              <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity z-20">
                <input
                  type="file"
                  ref={(el) => (fileInputRefs.current[index] = el)}
                  className="hidden"
                  accept="image/*"
                  onChange={(e) => onFileSelect(e, index)}
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRefs.current[index]?.click();
                  }}
                  className="p-1.5 rounded-full backdrop-blur border transition-all duration-200"
                  style={{ backgroundColor: 'var(--overlay-medium)', color: 'var(--color-text-primary)', borderColor: 'var(--color-border-primary)' }}
                  title="本地上传图片"
                >
                  <Upload size={12} />
                </button>
              </div>
            </div>
            <InsertTrigger index={index + 1} />
          </React.Fragment>
        ))}
        <div className="w-10 shrink-0"></div>
      </div>

      {/* 图片资源选择器 */}
      <ResourceSelector
        isOpen={showImageResourceSelector}
        onClose={() => setShowImageResourceSelector(false)}
        onSelect={handleSelectResource}
        projectId={projectId}
        type="image"
        title="选择图片资源"
      />

      {/* 视频资源选择器 */}
      {videoEnabled && (
        <ResourceSelector
          isOpen={showVideoResourceSelector}
          onClose={() => setShowVideoResourceSelector(false)}
          onSelect={handleSelectResource}
          projectId={projectId}
          type="video"
          title="选择视频资源"
        />
      )}

      {/* 删除确认弹窗 */}
      <ConfirmDialog
        isOpen={deleteConfirmIndex !== null}
        onClose={() => setDeleteConfirmIndex(null)}
        onConfirm={() => {
          if (deleteConfirmIndex !== null) {
            handleDeleteRow(deleteConfirmIndex);
            setDeleteConfirmIndex(null);
          }
        }}
        title="删除分镜"
        message="确定要删除此镜头吗？此操作不可撤销。"
        variant="danger"
      />
    </div>
  );
};

export default ShotGalleryView;
